import type { ReactNode } from 'react';
import { Inbox } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface EmptyStateProps {
  title: ReactNode;
  /** Tell the person what to do next (ARCHITECTURE §8: every list has an empty state). */
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  headingLevel?: 2 | 3;
  className?: string;
}

export function EmptyState({ title, description, icon, action, headingLevel = 2, className }: EmptyStateProps) {
  const H = `h${headingLevel}` as 'h2' | 'h3';
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 rounded-[var(--radius-card)] border-2 border-dashed border-border bg-surface px-4 py-10 text-center',
        className,
      )}
    >
      <span aria-hidden="true" className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary-soft text-primary">
        {icon ?? <Inbox className="h-7 w-7" />}
      </span>
      <H className="text-xl">{title}</H>
      {description && <div className="max-w-prose text-muted">{description}</div>}
      {action && <div className="mt-2 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  );
}

export interface SkeletonProps {
  className?: string;
  /** Render several text-line placeholders. */
  lines?: number;
}

/** Loading placeholder. Decorative (aria-hidden): pair with a visually hidden "Loading" status. */
export function Skeleton({ className, lines }: SkeletonProps) {
  if (lines && lines > 1) {
    return (
      <div aria-hidden="true" className="flex flex-col gap-2">
        {Array.from({ length: lines }, (_, i) => (
          <div
            key={i}
            className={cn('st-animate-pulse h-4 rounded bg-surface-sunken', i === lines - 1 && 'w-2/3', className)}
          />
        ))}
      </div>
    );
  }
  return <div aria-hidden="true" className={cn('st-animate-pulse h-4 rounded bg-surface-sunken', className)} />;
}

/** Card-shaped skeleton with a screen-reader loading status. */
export function SkeletonCard({ label = 'Loading', lines = 3 }: { label?: string; lines?: number }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4" role="status">
      <span className="sr-only">{label}</span>
      <Skeleton className="mb-3 h-6 w-1/3" />
      <Skeleton lines={lines} />
    </div>
  );
}
